import { IconLogOut, IconSettings, IconUser } from 'featherico';
import type * as React from 'react';
import type { CurrentUserDto } from '../../../../../../types/UserDtos';
import type { NavStyle } from '../../../../../App/AppShell/useNavStyle';
import { logout } from '../../../../../api/custom/UserLogout';
import * as ROUTES from '../../../../../routes';
import Avatar from '../../../../components/Avatar';
import Button from '../../../../components/Button';
import { DropdownMenu, MenuItem, MenuLink, MenuSeparator } from '../../../../components/DropdownMenu';
import IconPlaceholder from '../../../../components/IconPlaceholder';
import replacement from '../../../../services/Replacement';
import { goToUrl } from '../../../../services/Router';
import { prependAppPath } from '../../../../services/Url';
import { getLogoutRedirectUrl, isAnyAdmin } from '../../../../services/User';
import styles from './style.m.less';

type Props = {
    user: CurrentUserDto;
    navStyle: NavStyle;
};

const UserMenu: React.FC<Props> = ({ user, navStyle }) => {
    return (
        <DropdownMenu
            trigger={
                <Button
                    kind="PLAIN"
                    className={
                        {
                            hidden: styles.userDropdownButtonHiddenNav,
                            compact: styles.userDropdownButtonCompact,
                            wide: styles.userDropdownButton,
                        }[navStyle]
                    }
                    aria-label={`You're logged in as ${user.firstName}. Click to open user settings menu.`}
                >
                    <UserButton user={user} />
                </Button>
            }
            triggerWidthAligned
            placement={navStyle === 'compact' ? 'right-end' : 'top-start'}
            eventTracking={{ category: 'navigation user menu', action: 'toggle' }}
        >
            {!user.isGuest && (
                <>
                    <MenuLink
                        href={prependAppPath(ROUTES.USER_PROFILE_SHORT)}
                        eventTracking={{ category: 'navigation user menu', action: 'click', label: 'profile' }}
                    >
                        <IconUser /> Profile
                    </MenuLink>
                    <MenuLink
                        href={prependAppPath(ROUTES.USER_SETTINGS)}
                        eventTracking={{
                            category: 'navigation user menu',
                            action: 'click',
                            label: 'your settings',
                        }}
                    >
                        <IconSettings /> Settings
                    </MenuLink>
                </>
            )}
            <MenuLink
                href="/backups"
                eventTracking={{ category: 'navigation user menu', action: 'click', label: 'backups' }}
            >
                <IconPlaceholder /> Backups
            </MenuLink>
            {!user.isGuest && (
                <MenuLink
                    href="/app/downloads"
                    eventTracking={{ category: 'navigation user menu', action: 'click', label: 'exports' }}
                >
                    <IconPlaceholder /> Exports
                </MenuLink>
            )}
            {!user.isGuest && !isAnyAdmin(user) && (
                <MenuLink
                    href={prependAppPath(ROUTES.PEOPLE_DIRECTORY)}
                    eventTracking={{
                        category: 'navigation user menu',
                        action: 'click',
                        label: 'company directory',
                    }}
                >
                    <IconPlaceholder /> {replacement('company.big')} Directory
                </MenuLink>
            )}
            <MenuSeparator />
            <MenuItem
                onClick={() => {
                    logout().then(() => goToUrl(getLogoutRedirectUrl(user)));
                }}
                eventTracking={{ category: 'navigation user menu', action: 'click', label: 'logout' }}
            >
                <IconLogOut /> Log Out
            </MenuItem>
            {user.isSmallImprovements && (
                <>
                    <MenuSeparator />
                    <MenuLink href="/app/observer/companies">
                        <IconPlaceholder /> Company Observer
                    </MenuLink>
                    <MenuLink href="/app/observer/users">
                        <IconPlaceholder /> User Observer
                    </MenuLink>
                    <MenuLink href="/app/observer/experiments">
                        <IconPlaceholder /> Experiment Observer
                    </MenuLink>
                    <MenuLink href="/app/observer/ai-coach">
                        <IconPlaceholder /> AI Coach Observer
                    </MenuLink>
                    <MenuLink href="/app/observer/users-to-emails">
                        <IconPlaceholder /> Users to Emails Tool
                    </MenuLink>
                    <MenuLink href="/app/observer/features">
                        <IconPlaceholder /> Bulk Changes
                    </MenuLink>
                    <MenuLink href="/app/observer/customer-health">
                        <IconPlaceholder /> Customer Health
                    </MenuLink>
                    <MenuLink href="/app/system-access-roles">
                        <IconPlaceholder /> System Access Roles
                    </MenuLink>
                    <MenuLink href="/paymentObserver">
                        <IconPlaceholder /> Payment Observer
                    </MenuLink>
                </>
            )}
        </DropdownMenu>
    );
};

function UserButton({ user }: { user: CurrentUserDto }) {
    return (
        <>
            <Avatar user={user} size="MD" />
            <div className={styles.userDropdownButtonLabel}>
                {user.name} {user.impersonator && <>[{user.impersonator.firstName}]</>}
            </div>
        </>
    );
}

export default UserMenu;
